/**
 * Décision de repli quand le flux en cours devient inexploitable.
 *
 * Logique pure : `VideoHlsView` observe les symptômes et applique la liste d'exclusion et le
 * plafond rendus ici en les passant à `selectStream`.
 *
 * Deux causes, deux réponses. Un gel alors que le débit mesuré suffit accuse le flux : on l'écarte.
 * Un gel alors que le débit ne suit pas accuse la connexion : écarter un flux sain enverrait le
 * spectateur sur plus lourd, on abaisse donc un plafond. Sur la qualité la plus basse il n'y a rien
 * en dessous, et le flux est écarté comme défaillant.
 *
 * Le déclencheur ne peut pas se limiter aux gels : un egress mort réécrit sa playlist en VOD, le
 * lecteur met alors la vidéo en pause, et une vidéo en pause ne produit aucun gel observable.
 */

import type { PlaybackStreamQuality } from '@/features/live/types';
import { tierFromBandwidth } from '@/lib/connectionQuality';
import { STALL_MIN_DURATION_MS } from '@/lib/playbackStallTracker';
import { QUALITY_ORDER, qualityForConnection } from '@/lib/streamSelection';

/** Fenêtre glissante sur laquelle on compte les gels. */
export const DOWNGRADE_WINDOW_MS = 30_000;

/** Nombre de gels dans la fenêtre au-delà duquel le flux est jugé inexploitable. */
export const DOWNGRADE_STALL_THRESHOLD = 3;

/** Un seul gel de trois secondes suffit : le spectateur a déjà perdu le fil. */
export const DOWNGRADE_SINGLE_STALL_MS = 3_000;

/** Délai minimal entre deux changements, dans un sens comme dans l'autre. */
export const DOWNGRADE_COOLDOWN_MS = 60_000;

/**
 * Durée sans incident avant de retenter le flux écarté. Elle double à chaque repli : sur un flux
 * réellement mort, y revenir toutes les deux minutes ferait osciller le spectateur indéfiniment.
 */
export const RECOVERY_BASE_MS = 120_000;
export const RECOVERY_MAX_MS = 900_000;

/** On ne peut pas tout écarter : il faut toujours laisser un flux à jouer. */
export const MAX_AVOIDED_STREAMS = 3;

export type DowngradeState = {
  /** Qualités écartées, la plus récemment écartée en dernier. */
  avoided: PlaybackStreamQuality[];
  /** Qualité maximale après un gel dû au débit du spectateur, `null` sans plafond. */
  bandwidthCeiling: PlaybackStreamQuality | null;
  /** Replis déjà décidés, y compris ceux annulés depuis. Pilote l'allongement du délai de retour. */
  downgradeCount: number;
  /** Horodatages des gels retenus, tronqués à la fenêtre. */
  recentStallsMs: number[];
  /** Dernier changement, pour le délai de garde. */
  lastChangeAtMs: number;
  /** Verdict de flux mort encore à appliquer : il accuse le flux, quel que soit le débit. */
  streamDeadAtMs: number | null;
};

export const createDowngradeState = (): DowngradeState => ({
  avoided: [],
  bandwidthCeiling: null,
  downgradeCount: 0,
  recentStallsMs: [],
  lastChangeAtMs: 0,
  streamDeadAtMs: null,
});

const recoveryDelayMs = (downgradeCount: number): number =>
  Math.min(RECOVERY_BASE_MS * 2 ** Math.max(0, downgradeCount - 1), RECOVERY_MAX_MS);

const rank = (quality: PlaybackStreamQuality): number => QUALITY_ORDER.indexOf(quality);

/** Un cran au-dessus, ou plus de plafond du tout une fois la qualité maximale atteinte. */
const raiseCeiling = (ceiling: PlaybackStreamQuality): PlaybackStreamQuality | null =>
  QUALITY_ORDER[rank(ceiling) + 1] ?? null;

/**
 * Enregistre un gel terminé. Les gels trop courts sont du jitter de décodage et ne comptent pas,
 * même seuil que la remontée Bugsnag pour que les deux mesures racontent la même histoire.
 */
export function recordStall(
  state: DowngradeState,
  endedAtMs: number,
  durationMs: number,
): DowngradeState {
  if (durationMs < STALL_MIN_DURATION_MS) return state;
  const kept = [...state.recentStallsMs, endedAtMs].filter(
    (at) => endedAtMs - at < DOWNGRADE_WINDOW_MS,
  );
  const weight = durationMs >= DOWNGRADE_SINGLE_STALL_MS ? DOWNGRADE_STALL_THRESHOLD : 1;
  const padded =
    weight > 1 ? [...kept, ...Array.from({ length: weight - 1 }, () => endedAtMs)] : kept;
  return { ...state, recentStallsMs: padded };
}

/**
 * Verdict immédiat sur le flux courant : playlist devenue VOD alors que la session est en direct,
 * ou abandon définitif du lecteur. Inutile d'attendre un gel qui ne viendra pas.
 */
export function recordStreamDead(state: DowngradeState, atMs: number): DowngradeState {
  const kept = state.recentStallsMs.filter((at) => atMs - at < DOWNGRADE_WINDOW_MS);
  return {
    ...state,
    recentStallsMs: [...kept, ...Array.from({ length: DOWNGRADE_STALL_THRESHOLD }, () => atMs)],
    streamDeadAtMs: atMs,
  };
}

export type DowngradeContext = {
  /** Qualité réellement jouée, celle qu'on écartera si elle se révèle inexploitable. */
  currentQuality?: PlaybackStreamQuality | null;
  /** Début du gel encore en cours, s'il y en a un. */
  pendingStallSinceMs?: number | null;
  /**
   * Débit mesuré sur les segments, en bits par seconde. `null` tant qu'il n'est pas fiable
   * (lecture native d'Apple, début de lecture) : le gel accuse alors le flux, comme avant.
   */
  bandwidthBps?: number | null;
};

/**
 * Décide de la liste d'exclusion et du plafond. Appelée après chaque gel, à chaque battement, et
 * sur verdict.
 *
 * Le gel **en cours** compte autant qu'un gel terminé : un flux coupé ne repart jamais, donc il ne
 * produirait aucun gel terminé et le repli ne partirait jamais.
 */
export function evaluateDowngrade(
  state: DowngradeState,
  nowMs: number,
  context?: DowngradeContext,
): DowngradeState {
  const recent = state.recentStallsMs.filter((at) => nowMs - at < DOWNGRADE_WINDOW_MS);
  const deadVerdict =
    state.streamDeadAtMs !== null && nowMs - state.streamDeadAtMs < DOWNGRADE_WINDOW_MS;
  const settled = {
    ...state,
    recentStallsMs: recent,
    streamDeadAtMs: deadVerdict ? state.streamDeadAtMs : null,
  };

  if (nowMs - settled.lastChangeAtMs < DOWNGRADE_COOLDOWN_MS) return settled;

  const pendingSince = context?.pendingStallSinceMs ?? null;
  const pendingIsDecisive =
    pendingSince !== null && nowMs - pendingSince >= DOWNGRADE_SINGLE_STALL_MS;
  const shouldDowngrade = pendingIsDecisive || recent.length >= DOWNGRADE_STALL_THRESHOLD;

  const current = context?.currentQuality ?? null;
  if (shouldDowngrade && current !== null) {
    const bandwidthBps = context?.bandwidthBps ?? null;
    const affordable =
      !deadVerdict && bandwidthBps !== null && bandwidthBps > 0
        ? qualityForConnection(tierFromBandwidth(bandwidthBps))
        : null;
    if (affordable !== null && rank(affordable) < rank(current)) {
      return {
        ...settled,
        bandwidthCeiling: affordable,
        downgradeCount: settled.downgradeCount + 1,
        recentStallsMs: [],
        lastChangeAtMs: nowMs,
      };
    }
    if (!settled.avoided.includes(current) && settled.avoided.length < MAX_AVOIDED_STREAMS) {
      return {
        ...settled,
        avoided: [...settled.avoided, current],
        downgradeCount: settled.downgradeCount + 1,
        // Les gels déjà payés par ce repli ne doivent pas en déclencher un second aussitôt.
        recentStallsMs: [],
        lastChangeAtMs: nowMs,
        streamDeadAtMs: null,
      };
    }
  }

  // Un gel en cours interdit le retour en arrière, même court : rien n'est sain à cet instant.
  if (pendingSince !== null) return settled;

  const lastStallMs = recent.length > 0 ? Math.max(...recent) : settled.lastChangeAtMs;
  const healthyForMs = nowMs - Math.max(lastStallMs, settled.lastChangeAtMs);
  if (healthyForMs < recoveryDelayMs(settled.downgradeCount)) return settled;

  // Le plafond se lève en premier, un cran à la fois : une connexion qui faiblit se rétablit
  // souvent, et le débit mesuré garde le dernier mot dans `selectStream`.
  if (settled.bandwidthCeiling !== null) {
    return {
      ...settled,
      bandwidthCeiling: raiseCeiling(settled.bandwidthCeiling),
      lastChangeAtMs: nowMs,
    };
  }
  if (settled.avoided.length > 0) {
    return { ...settled, avoided: settled.avoided.slice(0, -1), lastChangeAtMs: nowMs };
  }

  return settled;
}
